/**
 * Taille d'affichage du cockpit — l'équivalent interne du zoom navigateur,
 * avec un mode AUTO qui s'adapte à l'écran.
 *
 * 🩸 Pourquoi ça existe : le cockpit ne force AUCUNE échelle (vérifié le
 * 20-08-2026 sur le CSS servi — pas un seul `zoom:`, pas de `html{font-size}`,
 * pas de `:root{font-size}`). Il rend donc à 100 % de ce que la machine lui
 * donne, et **Windows met 125 % d'échelle par défaut** sur la plupart des
 * portables. D'où deux employées côte à côte, même écran, deux densités — l'une
 * voit dix colonnes de conversations, l'autre cinq, sans que personne ait
 * touché à quoi que ce soit.
 *
 * Le réglage est PAR APPAREIL (localStorage) : ce qui varie, c'est la machine,
 * pas la personne. Une chatteuse qui change de poste doit re-régler, et c'est
 * juste — c'est l'écran qu'on règle.
 *
 * 🪤 `zoom` se pose sur `<body>`, JAMAIS sur `<html>`. Mesuré dans Chrome 148 :
 * sur la racine il est bien *calculé* (`getComputedStyle` rend « 0.8 ») et
 * strictement sans effet — la largeur de mise en page ne bouge pas d'un pixel.
 * Sur `<body>` il donne exactement la sémantique du zoom navigateur :
 * `clientWidth` passe de 1280 à 1600 à 80 %, à 1024 à 125 %. C'est cette
 * largeur-là que mesurent les grilles (celle des conversations calcule ses
 * colonnes sur `clientWidth`) — donc dézoomer donne vraiment PLUS de colonnes,
 * pas juste du texte plus petit.
 *
 * Ce qui a été vérifié avec, parce que c'est ce qui casse d'habitude :
 *   · un `position:fixed; inset:0` (le fond des modales) mesure toujours le
 *     viewport exact — 1280×720 à 80 % comme à 125 %. Les overlays tiennent.
 *   · `useIsMobile` lit `window.innerWidth`, que `zoom` ne touche pas → aucun
 *     risque de basculer sur le shell mobile, où les chatteurs sont BLOQUÉS.
 *   · les media queries ne suivent PAS (`min-width:1600px` reste faux à 80 %
 *     sur une fenêtre de 1280). Les variantes `lg:`/`xl:` continuent donc de se
 *     lire sur la vraie fenêtre — c'est le sens prudent : dézoomer donne de la
 *     place sans faire changer le shell de mode.
 *   · ⛔ mais un `position:fixed` placé au CURSEUR se décale : `left:400px`
 *     peint à 320 px à 80 %. Tout menu ancré sur `clientX/clientY` doit passer
 *     par `curseurVersMiseEnPage` — c'est à ça qu'elle sert.
 */

/** Les crans manuels proposés dans les réglages, en pourcent. */
export const TAILLES_AFFICHAGE = [70, 80, 90, 100, 110, 125] as const;

/** « auto » = calculé sur la largeur de la fenêtre ; un nombre = choisi à la main. */
export type TailleAffichage = number | "auto";

/** Le défaut est AUTO : les 36 chatteurs n'ont rien à régler pour en profiter. */
export const TAILLE_AFFICHAGE_DEFAUT: TailleAffichage = "auto";

const TAILLE_MIN = 70;
const TAILLE_MAX = 125;

/**
 * Le plancher du mode auto. En dessous, on préfère montrer moins que rendre
 * illisible — et un chatteur qui veut aller plus loin a les crans manuels.
 */
const AUTO_MIN = 70;

/** L'écran tel que le navigateur le décrit, pour le mode auto. */
export interface ContexteEcran {
  /** `devicePixelRatio` : sur Windows ≈ échelle du système × zoom navigateur. */
  densite: number;
  /** Le calcul n'est valable que là où « échelle » veut dire « lisibilité ». */
  estWindows: boolean;
}

export function contexteEcran(): ContexteEcran {
  if (typeof window === "undefined") return { densite: 1, estWindows: false };
  const ua = window.navigator?.userAgent ?? "";
  return {
    densite: window.devicePixelRatio || 1,
    estWindows: /Windows/i.test(ua),
  };
}

/**
 * Ramène une valeur (stockée, corrompue, ancienne, ou saisie à la main dans
 * localStorage) sur quelque chose d'utilisable. Tout ce qui n'est ni « auto »
 * ni un nombre fini retombe sur le défaut : un cockpit illisible est pire
 * qu'un cockpit non réglé.
 */
export function normaliserTaille(valeur: unknown): TailleAffichage {
  if (valeur === "auto") return "auto";
  // 🪤 Pas de `Number(valeur)` en vrac : `Number(null)` vaut 0, `Number("")`
  // aussi, et `Number(false)` encore — tous finis, donc tous ramenés au
  // plancher de 70 % au lieu de retomber sur « auto ». Attrapé par le test.
  const n =
    typeof valeur === "number"
      ? valeur
      : typeof valeur === "string" && valeur.trim() !== ""
        ? Number(valeur)
        : Number.NaN;
  if (!Number.isFinite(n)) return TAILLE_AFFICHAGE_DEFAUT;
  return Math.min(TAILLE_MAX, Math.max(TAILLE_MIN, Math.round(n)));
}

/**
 * Le pourcentage que choisit le mode auto. **Il défait l'agrandissement, il ne
 * combat pas la taille de l'écran** — c'est toute la règle, et c'est ce qui la
 * rend sûre.
 *
 * 🪤 Une première version visait une largeur utile de 1920 px à partir de
 * `innerWidth`. Elle confondait deux choses que la largeur ne distingue pas :
 * une fenêtre de 1536 px parce que Windows est à 125 % (il FAUT dézoomer), et
 * une fenêtre de 1536 px parce que l'écran est petit ou pas maximisé (il ne
 * faut surtout PAS). Elle aurait rendu illisible un 1366 qui n'avait rien
 * demandé. `devicePixelRatio`, lui, dit exactement ce qu'on cherche : de
 * combien la machine a grossi les pixels.
 *
 * Windows seulement, et ce n'est pas de la paresse : `devicePixelRatio` mélange
 * deux causes, l'échelle décidée pour la lisibilité et la finesse physique de
 * la dalle. Sur Windows la seconde est ~constante (≈96 ppp), donc la densité
 * est l'échelle. Sur un Retina la densité vaut 2 sans que rien soit « trop
 * gros » — dézoomer y serait une faute. Ailleurs, on ne devine pas : 100 %.
 *
 * Bonus de la formule : elle se combine avec le zoom NAVIGATEUR au lieu de s'y
 * ajouter. Quelqu'un déjà à 80 % dans Chrome a une densité de 1.25 × 0.8 = 1 →
 * auto ne retouche rien.
 *
 * Deux garde-fous :
 *   · on n'AGRANDIT jamais (plafond 100 %) — personne n'a demandé plus gros ;
 *   · on ne descend pas sous 70 %, même sur une machine à 200 %.
 */
export function tailleAutoPourEcran({ densite, estWindows }: ContexteEcran): number {
  if (!estWindows) return 100;
  if (!Number.isFinite(densite) || densite <= 0) return 100;
  const brut = 100 / densite;
  const arrondi = Math.round(brut / 5) * 5;
  return Math.min(100, Math.max(AUTO_MIN, arrondi));
}

/** Le pourcentage effectif : résout « auto » contre l'écran courant. */
export function resoudreTaille(
  valeur: unknown,
  ecran: ContexteEcran = contexteEcran(),
): number {
  const taille = normaliserTaille(valeur);
  return taille === "auto" ? tailleAutoPourEcran(ecran) : taille;
}

/**
 * Applique la taille sur `<body>`. À 100 % on RETIRE la propriété au lieu
 * d'écrire `zoom: 1` — un appareil qui n'a rien à corriger doit être
 * exactement dans l'état d'avant ce réglage, sans une propriété de plus.
 */
export function appliquerTailleAffichage(body: HTMLElement, pourcent: number): void {
  if (!Number.isFinite(pourcent) || pourcent === 100) {
    body.style.removeProperty("zoom");
    body.style.removeProperty("--zoom-affichage");
    return;
  }
  const facteur = pourcent / 100;
  body.style.zoom = String(facteur);
  // ⛔ `zoom` ne met PAS les unités de viewport à l'échelle : `100dvh` vaut la
  // hauteur de la fenêtre quoi qu'il arrive, donc le shell se peignait à 576 px
  // dans une fenêtre de 720 à 80 % (bande vide en bas) et à 900 px à 125 %
  // (coupé). Les `dvh`/`dvw` du shell se divisent par cette variable.
  body.style.setProperty("--zoom-affichage", String(facteur));
}

/**
 * Le facteur de zoom actuellement appliqué (1 si aucun). Lu sur le style
 * calculé plutôt que sur l'atom : ça marche depuis n'importe où, y compris
 * hors React, et ça ne peut pas désynchroniser.
 */
export function zoomAffichage(body: HTMLElement = document.body): number {
  const brut = Number(window.getComputedStyle(body).zoom);
  return Number.isFinite(brut) && brut > 0 ? brut : 1;
}

/**
 * Convertit des coordonnées ÉCRAN (`clientX`/`clientY` d'un événement) en
 * coordonnées de MISE EN PAGE, les seules que comprend un `position: fixed`
 * rendu sous un `<body>` zoomé.
 *
 * ⛔ Sans elle, un menu de clic droit atterrit à `x × zoom` : à 80 %, un clic
 * à 1 200 px ouvre le menu à 960. Mesuré dans Chrome 148, pas déduit.
 */
/**
 * La fenêtre exprimée en pixels de MISE EN PAGE — ce que doit utiliser tout
 * calage contre le bord de l'écran (un menu qui déborderait en bas).
 *
 * ⛔ `window.innerWidth/innerHeight` sont en pixels ÉCRAN. Les comparer à un
 * `getBoundingClientRect()` est correct — les deux sont en écran — mais le
 * résultat est ensuite écrit dans un `top`/`left`, qui est en mise en page :
 * le menu se recale alors à côté. Même piège que `curseurVersMiseEnPage`, un
 * cran plus loin.
 */
export function viewportMiseEnPage(body: HTMLElement = document.body): {
  largeur: number;
  hauteur: number;
} {
  const z = zoomAffichage(body);
  return { largeur: window.innerWidth / z, hauteur: window.innerHeight / z };
}

export function curseurVersMiseEnPage(
  x: number,
  y: number,
  body: HTMLElement = document.body,
): { x: number; y: number } {
  const z = zoomAffichage(body);
  if (z === 1) return { x, y };
  return { x: x / z, y: y / z };
}
